import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Bell, History, House, LogOut, Settings, Shield, Trash2, Users } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useHousehold } from '../lib/household'

/** Gabarit des pages connectées. Chaque icône est accompagnée de son texte. */
export function Layout({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  const { t } = useTranslation()
  const { me, logout } = useAuth()
  const { households, current, select } = useHousehold()
  const navigate = useNavigate()
  const simplified = me?.user.simplifiedMode ?? true
  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    enabled: Boolean(me),
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  })
  const unreadCount = unread.data?.count ?? 0

  return (
    <>
      <a className="skip-link" href="#main">
        {t('common.skipToContent')}
      </a>
      <header className="header">
        <Link to="/" className="brand">
          <House aria-hidden size={28} />
          {t('app.name')}
        </Link>
        <nav className="nav" aria-label={t('app.name')}>
          {households.length > 1 && (
            <div className="field">
              <label htmlFor="household-picker">
                {t('nav.householdPicker')}
              </label>
              <select id="household-picker" value={current?.id ?? ''} onChange={(e) => select(e.target.value)}>
                {households.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Link className="btn ghost" to="/activity">
            <History aria-hidden size={20} />
            {t('nav.activity')}
          </Link>
          {/* Pas d'aria-label : le nom accessible reprend le texte visible, nombre de non lues compris. */}
          <Link className="btn ghost" to="/notifications">
            <Bell aria-hidden size={20} />
            {t('nav.notifications')}
            {unreadCount > 0 ? ` (${unreadCount})` : ''}
          </Link>
          <Link className="btn ghost" to="/trash">
            <Trash2 aria-hidden size={20} />
            {t('nav.trash')}
          </Link>
          <Link className="btn ghost" to="/household">
            <Users aria-hidden size={20} />
            {t('nav.household')}
          </Link>
          <Link className="btn ghost" to="/settings">
            <Settings aria-hidden size={20} />
            {t('nav.settings')}
          </Link>
          {me?.user.isInstanceAdmin && !simplified && (
            <Link className="btn ghost" to="/admin">
              <Shield aria-hidden size={20} />
              {t('nav.admin')}
            </Link>
          )}
          <button
            type="button"
            className="btn ghost"
            onClick={async () => {
              await logout()
              navigate('/login')
            }}
          >
            <LogOut aria-hidden size={20} />
            {t('nav.logout')}
          </button>
        </nav>
      </header>
      <main id="main" className={wide ? 'wide' : undefined} tabIndex={-1}>
        {current?.readOnly && <div className="alert warning">{t('household.readOnly')}</div>}
        {children}
      </main>
      <SiteFooter />
    </>
  )
}

/** Pied de page commun : aide en vidéo et confidentialité. */
function SiteFooter() {
  const { t } = useTranslation()
  return (
    <footer className="footer row">
      <Link to="/aide">{t('help.link')}</Link>
      <Link to="/confidentialite">{t('privacy.footerLink')}</Link>
    </footer>
  )
}

/** Gabarit des pages publiques (connexion, inscription…). */
export function PublicLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  return (
    <>
      <header className="header">
        <Link to="/" className="brand">
          <House aria-hidden size={28} />
          {t('app.name')}
        </Link>
      </header>
      <main id="main">{children}</main>
      <SiteFooter />
    </>
  )
}
